import {
  BLACK,
  type Board,
  type Color,
  DIRS,
  type Dir,
  type Move,
  N,
  type Position,
  WHITE,
} from './types';

export function inBounds(r: number, c: number): boolean {
  return r >= 0 && r < N && c >= 0 && c < N;
}

export function opponent(color: Color): Color {
  return color === BLACK ? WHITE : BLACK;
}

/**
 * Returns all legal capture jumps from (r, c). If `constrainDir` is supplied,
 * only that direction is considered (used during multi-capture continuations).
 */
export function captures(board: Board, r: number, c: number, constrainDir?: Dir): Move[] {
  const color = board[r][c];
  if (!color) return [];
  const opp = opponent(color);
  const dirs = constrainDir ? [constrainDir] : DIRS;
  const result: Move[] = [];
  for (const dir of dirs) {
    const [dr, dc] = dir;
    const mr = r + dr;
    const mc = c + dc;
    const lr = r + 2 * dr;
    const lc = c + 2 * dc;
    if (inBounds(mr, mc) && inBounds(lr, lc) && board[mr][mc] === opp && board[lr][lc] === null) {
      result.push({ fr: r, fc: c, tr: lr, tc: lc, cr: mr, cc: mc, dir });
    }
  }
  return result;
}

export function anyMoves(board: Board, color: Color): boolean {
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      if (board[r][c] === color && captures(board, r, c).length > 0) {
        return true;
      }
    }
  }
  return false;
}

/** Positions of pieces of `color` that have at least one legal capture. */
export function movablePieces(board: Board, color: Color): Position[] {
  const result: Position[] = [];
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      if (board[r][c] === color && captures(board, r, c).length > 0) {
        result.push({ r, c });
      }
    }
  }
  return result;
}

/**
 * Squares Black may remove for the opening move: one of the four center
 * squares or one of the four corners, restricted to squares that actually
 * hold a black stone (on the standard checkerboard layout only 2 of each).
 */
export function setupBlackOptions(board: Board): Position[] {
  const candidates: Position[] = [
    { r: 3, c: 3 },
    { r: 3, c: 4 },
    { r: 4, c: 3 },
    { r: 4, c: 4 },
    { r: 0, c: 0 },
    { r: 0, c: N - 1 },
    { r: N - 1, c: 0 },
    { r: N - 1, c: N - 1 },
  ];
  return candidates.filter(({ r, c }) => board[r][c] === BLACK);
}

/** Squares White may remove for the opening: white stones orthogonally adjacent to (br, bc). */
export function setupWhiteOptions(board: Board, br: number, bc: number): Position[] {
  const result: Position[] = [];
  for (const [dr, dc] of DIRS) {
    const r = br + dr;
    const c = bc + dc;
    if (inBounds(r, c) && board[r][c] === WHITE) {
      result.push({ r, c });
    }
  }
  return result;
}
